<script setup>
// Deterministic SVG identicon — no uploaded images. Generic/reusable across
// any feature that needs a stable per-identity avatar (currently NEXCEO's AI
// Workforce cards; a distinct component from Avatar.vue in this same folder,
// which is Chatwoot's image+initials avatar for contacts/agents). Callers
// supply `color` directly so this component never depends on any
// feature-specific color/department system.
import { computed } from 'vue';
import { generateAvatar } from './generateAvatar';

const props = defineProps({
  name: { type: String, required: true },
  color: { type: String, default: '#6b7280' },
  variant: { type: String, default: '' },
  seed: { type: String, default: '' },
  size: { type: Number, default: 40 },
});

const avatar = computed(() =>
  generateAvatar({ name: props.name, variant: props.variant, seed: props.seed })
);

const center = computed(() => props.size / 2);
const radius = computed(() => props.size / 2 - 1);
const ringRadius = computed(() => radius.value - 2.5);
const fontSize = computed(() => props.size * 0.36);

const ringDots = computed(() =>
  avatar.value.ring.map((on, i) => {
    const angle = (i / avatar.value.ring.length) * 2 * Math.PI - Math.PI / 2;
    return {
      on,
      cx: center.value + ringRadius.value * Math.cos(angle),
      cy: center.value + ringRadius.value * Math.sin(angle),
    };
  })
);
</script>

<template>
  <svg
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
    role="img"
    :aria-label="name"
    class="shrink-0"
  >
    <circle
      :cx="center"
      :cy="center"
      :r="radius"
      :fill="color"
      fill-opacity="0.14"
      :stroke="color"
      stroke-width="1.5"
    />
    <circle
      v-for="(dot, i) in ringDots"
      :key="i"
      :cx="dot.cx"
      :cy="dot.cy"
      :r="size * 0.028"
      :fill="color"
      :fill-opacity="dot.on ? 0.85 : 0.18"
    />
    <text
      :x="center"
      :y="center"
      :font-size="fontSize"
      :fill="color"
      text-anchor="middle"
      dominant-baseline="central"
      font-weight="700"
      font-family="ui-sans-serif, system-ui, sans-serif"
    >
      {{ avatar.initials }}
    </text>
  </svg>
</template>
